import isEmpty from "lodash-es/isEmpty";

import type { Preferences, UserInfo } from "@/api";
import { getImpersonationHeaders } from "@/utils";

import type { FeatureLimits } from "./features/slices/limit/models";
import { AppSettings, createAppSlice, type AppSlice } from "./app";
import { createEditorSlice, type EditorSlice } from "./editor";
import { createFeaturesSlice, type FeaturesFlags, type FeatureSlice } from "./features";
import { createProjectSlice, type ProjectSlice } from "./project";
import { createUserSlice, type UserSlice } from "./user";
import { exposeStoreToWindow, parsePreloadStateToBootstrap, removePreloadObject } from "./utils";

export type Bootstrap = {
  appSettings: AppSettings | undefined;
  features: FeaturesFlags;
  limits: FeatureLimits;
  preferences: Preferences | undefined;
  user: UserInfo | undefined;
};

export type Store = {
  user: UserSlice;
  features: FeatureSlice;
  project: ProjectSlice;
  app: AppSlice;
  editor: EditorSlice;
};

function createSlices(bootstrap: Bootstrap | undefined): Store {
  const userSlice = createUserSlice(bootstrap?.user, bootstrap?.preferences);
  const featureSlice = createFeaturesSlice(bootstrap?.features, bootstrap?.limits, userSlice);
  const appSlice = createAppSlice(bootstrap?.appSettings, featureSlice);
  const projectSlice = createProjectSlice(userSlice, featureSlice);

  return {
    app: appSlice,
    user: userSlice,
    features: featureSlice,
    project: projectSlice,
    editor: createEditorSlice(projectSlice, userSlice),
  };
}

export function createStore(): Store {
  let bootstrap;

  if (window.__PRELOADED_STATE__) {
    bootstrap = parsePreloadStateToBootstrap(window.__PRELOADED_STATE__);
    removePreloadObject();
  }
  const store = createSlices(bootstrap);

  if (store.features.isDev) {
    exposeStoreToWindow(store);
  }

  if (store.features.isAdmin) {
    store.app.isImpersonating.value = !isEmpty(getImpersonationHeaders());
  }

  return store;
}

export const Store = createStore();
